import {
  IRoleReference,
  IUserRoles,
  ReferenceType,
  ReferenceTypeNumerable,
  UserRoleEnum,
} from 'shared/models/User'

export const getRolesForClient = (roles: IUserRoles[], clientId?: string) => {
  if (!clientId) return []
  if (roles.length === 0) return []

  return roles.filter((role) =>
    role.reference.some(
      (ref) =>
        (ref.type === ReferenceType.ClientId ||
          (ref.type as unknown as ReferenceTypeNumerable) ===
            ReferenceTypeNumerable.ClientId) &&
        ref.value === clientId
    )
  )
}

export const containsBoardRole = (roles: string[]): boolean => {
  return roles.some(
    (x) =>
      x === UserRoleEnum.boardmember ||
      x === UserRoleEnum.boardleader ||
      x === UserRoleEnum.boardnextleader ||
      x === UserRoleEnum.alternateboardmember ||
      x === UserRoleEnum.secretary ||
      x === UserRoleEnum.cashier ||
      x === UserRoleEnum.committee
  )
}

export const hasBoardMemberRole = (role: UserRoleEnum) =>
  role === UserRoleEnum.boardmember ||
  role === UserRoleEnum.boardleader ||
  role === UserRoleEnum.boardnextleader

export const hasAuditorRole = (role: UserRoleEnum) =>
  role === UserRoleEnum.auditor

export const hasJanitorRole = (role: UserRoleEnum) =>
  role === UserRoleEnum.caretaker

export const containsStyeportalBruker = (roles: string[]): boolean => {
  return roles.some((x) => isStyrePortalBruker(x))
}

export const hasPortalAccess = (roles: string[]): boolean => {
  return roles.some(
    (x) =>
      isStyrePortalBruker(x) ||
      x === UserRoleEnum.caretaker ||
      x === UserRoleEnum.auditor ||
      x === UserRoleEnum.advisor ||
      x === UserRoleEnum.advisorFullAccess ||
      x === UserRoleEnum.digitalkeys ||
      x === UserRoleEnum.ceo
  )
}

export const isBoardRole = (role: string): boolean => {
  return containsBoardRole([role])
}

export const isDigitalKeysRole = (role: string): boolean => {
  return role === UserRoleEnum.digitalkeys
}

export const isStyrePortalBruker = (role: string): boolean => {
  return role === UserRoleEnum.portal
}

export const isCaretaker = (role: string): boolean => {
  return role === UserRoleEnum.caretaker
}

export const isAuditor = (role: string): boolean => {
  return role === UserRoleEnum.auditor
}

export const isAdvisor = (role: UserRoleEnum) => role === UserRoleEnum.advisor

export const isAdvisorWithFullAccess = (role: UserRoleEnum) =>
  role === UserRoleEnum.advisorFullAccess

export const isCEORole = (role: UserRoleEnum) => role === UserRoleEnum.ceo

export const hasCEORoleForClient = (
  roles: IUserRoles[] = [],
  clientId?: string
) => {
  const rolesForClient = getRolesForClient(roles, clientId)
  if (!rolesForClient.length) return false

  return rolesForClient.some(({ role }) => isCEORole(role))
}

export const hasBoardMemberRolesForClient = (
  roles: IUserRoles[] = [],
  clientId?: string
) => {
  const rolesForClient = getRolesForClient(roles, clientId)
  if (rolesForClient.length === 0) return false

  return rolesForClient.some(({ role }) =>
    hasBoardMemberRole(role as UserRoleEnum)
  )
}

export const hasJanitorRoleForClient = (
  roles: IUserRoles[] = [],
  clientId?: string
) => {
  const rolesForClient = getRolesForClient(roles, clientId)
  if (rolesForClient.length === 0) return false

  return rolesForClient.some(({ role }) => hasJanitorRole(role as UserRoleEnum))
}

export const hasAdvisorRolesForClient = (
  roles: IUserRoles[] = [],
  clientId?: string,
  fullAccess?: boolean
) => {
  const rolesForClient = getRolesForClient(roles, clientId)
  if (rolesForClient.length === 0) return false

  const hasAdvisorRole = rolesForClient.some(({ role }) =>
    fullAccess
      ? isAdvisorWithFullAccess(role as UserRoleEnum)
      : isAdvisor(role as UserRoleEnum)
  )

  const hasStyreportalBruker = rolesForClient.some(({ role }) =>
    isStyrePortalBruker(role)
  )

  return hasStyreportalBruker && hasAdvisorRole
}

export const hasBoardRoleForClient = (
  roles: IUserRoles[],
  clientId?: string
) => {
  const rolesForClient = getRolesForClient(roles, clientId)
  if (rolesForClient.length === 0) return false

  return rolesForClient.some(
    (r) => r.role === UserRoleEnum.portal || isBoardRole(r.role)
  )
}

export const hasOwnerAccess = (roles: IUserRoles[]) => {
  return roles.some((x) => x.role === UserRoleEnum.owner)
}

export const hasAuditorRolesForClient = (
  roles: IUserRoles[],
  clientId?: string
): boolean => {
  const rolesForClient = getRolesForClient(roles, clientId)
  if (rolesForClient.length === 0) return false

  return rolesForClient.some((r) => r.role === UserRoleEnum.auditor)
}

export const hasCaretakerRolesForClient = (
  roles: IUserRoles[],
  clientId?: string
): boolean => {
  const rolesForClient = getRolesForClient(roles, clientId)
  if (rolesForClient.length === 0) return false

  return rolesForClient.some((r) => r.role === UserRoleEnum.caretaker)
}

export const hasOnlyStyrePortalBrukerAndAdvisor = (
  roles: IUserRoles[],
  clientId?: string,
  fullAccess?: boolean
) => {
  const rolesForClient = getRolesForClient(roles, clientId)
  if (rolesForClient.length === 0) return false

  const hasAdvisorRole = rolesForClient.some((x) => {
    const role = x.role as UserRoleEnum
    return isAdvisor(role) || isAdvisorWithFullAccess(role)
  })

  if (!hasAdvisorRole) return false

  if (fullAccess) {
    return rolesForClient.every((x) => {
      const role = x.role as UserRoleEnum
      return isStyrePortalBruker(role) || isAdvisorWithFullAccess(role)
    })
  }

  return rolesForClient.every((x) => {
    const role = x.role as UserRoleEnum
    return isStyrePortalBruker(role) || isAdvisor(role)
  })
}

export const getClientIdsForUser = (userRoles?: IUserRoles[]): string[] => {
  if (!userRoles?.length) return []

  const checkRole = (role: string) =>
    isStyrePortalBruker(role) ||
    isBoardRole(role) ||
    isCaretaker(role) ||
    isAuditor(role) ||
    isDigitalKeysRole(role)

  const getAvailableClients = userRoles
    .filter(
      ({ role }) =>
        isStyrePortalBruker(role) ||
        isCaretaker(role) ||
        isAuditor(role) ||
        isDigitalKeysRole(role)
    )
    .flatMap((x) => x.reference.map((x) => x.value))

  return userRoles.reduce((distinctClientIds: string[], x: IUserRoles) => {
    // Must have atleast styreportalbruker
    if (checkRole(x.role)) {
      x.reference.forEach((reference: IRoleReference) => {
        // If we dont have the clientId in the distinct list
        // and we have styreportalbruker role for that client
        // We can add the reference to the list
        const canAddReference =
          !distinctClientIds.includes(reference.value) &&
          getAvailableClients.includes(reference.value)

        if (canAddReference) {
          distinctClientIds.push(reference.value)
        }
      })
    }
    return distinctClientIds
  }, [])
}

export const hasBoardRoleRoleForClient = (
  roles: IUserRoles[],
  clientId?: string
): boolean => {
  const rolesForClient = getRolesForClient(roles, clientId)
  if (rolesForClient.length === 0) return false

  return rolesForClient.some((r) => r.role === UserRoleEnum.caretaker)
}
